import { motion } from "framer-motion";
import { ArrowRight, Sparkles, MapPin, Phone } from "lucide-react";
import { useAboutMe, usePersonalInfo } from "@/hooks/usePortfolio";
import { getImageUrl } from "@/api/client";

const stats = [
  { v: "22+", l: "Years\nExperience" },
  { v: "₹73Cr", l: "Sales\nDelivered" },
  { v: "14+", l: "Awards &\nHonors" },
  { v: "50+", l: "Team\nLed" },
];

export const Hero = () => {
  const { data: aboutMe, isLoading: isLoadingAbout } = useAboutMe();
  const { data: personalInfo, isLoading: isLoadingPersonal } = usePersonalInfo();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
  };

  if (isLoadingAbout || isLoadingPersonal) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!aboutMe || !personalInfo) return null;

  const nameParts = (personalInfo.fullName || "").split(" ");
  const firstName = nameParts[0] || "Shivaji";
  const lastNameParts = nameParts.slice(1);
  const lastName = lastNameParts.length > 0 ? lastNameParts[0] : "Rajan";
  const initial = lastNameParts.length > 1 ? lastNameParts.slice(1).join(" ") : "M";

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center pt-28 pb-12 overflow-hidden">
      {/* Background ornaments */}
      <div className="absolute inset-0 -z-10 grain">
        <div className="absolute top-1/4 -left-32 w-[520px] h-[520px] rounded-full orb"
             style={{ background: "radial-gradient(closest-side, hsl(var(--gold)/0.16), transparent 70%)" }} />
        <div className="absolute bottom-0 -right-24 w-[560px] h-[560px] rounded-full orb-2"
             style={{ background: "radial-gradient(closest-side, hsl(var(--navy)/0.18), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.5]"
             style={{ backgroundImage: "linear-gradient(hsl(var(--foreground)/0.04) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)/0.04) 1px, transparent 1px)", backgroundSize: "80px 80px", maskImage: "radial-gradient(ellipse at center, black 25%, transparent 70%)" }} />
      </div>

      {/* Editorial vertical bars (left/right page numbers) */}
      <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 flex-col items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-muted-foreground rotate-180" style={{ writingMode: "vertical-rl" }}>
        <span>Portfolio · MMXXVI</span>
        <div className="h-12 w-px bg-gradient-to-b from-[hsl(var(--gold))]/60 to-transparent" />
      </div>

      <div className="container-x w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* LEFT — TEXT */}
          <div className="lg:col-span-7 relative">
            {/* Folio mark */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="font-serif italic text-sm text-muted-foreground">N<sup>o</sup> 01</span>
              <div className="hairline w-16" />
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]">
                <Sparkles className="w-3 h-3" /> 22+ Years of Leadership
              </div>
            </motion.div>

            {/* Eyebrow role */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="text-xs uppercase tracking-[0.45em] text-muted-foreground mb-4"
              data-testid="hero-eyebrow"
            >
              {personalInfo.designation || "Director · Sales · Business Leader"}
            </motion.div>

            {/* Display name */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 }}
              className="font-serif tracking-[-0.02em] leading-[0.92]"
              data-testid="hero-name"
            >
              <span className="block text-5xl sm:text-7xl lg:text-[7.5rem]">{firstName}</span>
              <span className="block text-5xl sm:text-7xl lg:text-[7.5rem] italic font-light mt-1">
                <span className="gold-text">{lastName}</span> <span className="text-foreground/85">{initial}</span>
              </span>
            </motion.h1>

            {/* Signature underline */}
            <motion.div
              initial={{ scaleX: 0, originX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.2, delay: 0.5 }}
              className="mt-7 h-px bg-gradient-to-r from-[hsl(var(--gold))] via-[hsl(var(--gold-deep))] to-transparent w-72"
            />

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-6 text-base md:text-lg text-foreground/80 max-w-xl leading-[1.7]"
              data-testid="hero-tagline"
            >
              {aboutMe.shortDescription || <><em className="text-foreground">Senior Business Management Professional</em> shaping enterprise sales, channel ecosystems and unified communications across India.</>}
            </motion.p>

            {/* CTAs + meta */}
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-4"
            >
              <button
                data-testid="hero-view-portfolio"
                onClick={() => scrollTo("experience")}
                className="btn-gold px-7 py-3.5 rounded-full text-sm font-medium tracking-wide inline-flex items-center gap-2 group"
              >
                View Portfolio
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-contact-me"
                onClick={() => scrollTo("contact")}
                className="btn-outline-luxury px-7 py-3.5 rounded-full text-sm font-medium tracking-wide"
              >
                Get in Touch
              </button>
              <div className="hidden md:flex items-center gap-4 ml-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[hsl(var(--gold))]" />{personalInfo.city}{personalInfo.country ? `, ${personalInfo.country}` : ''}</span>
                <span className="h-3 w-px bg-border" />
                <span className="inline-flex items-center gap-1.5"><Phone className="w-3 h-3 text-[hsl(var(--gold))]" />{personalInfo.phone}</span>
              </div>
            </motion.div>

            {/* Stats row — flush, editorial */}
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85 }}
              className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-y-6 md:gap-y-0 max-w-2xl border-t border-[hsl(var(--border))] pt-6 md:pt-0"
            >
              {stats.map((s, i) => (
                <div key={i} className={`md:py-5 ${i % 2 !== 0 ? "border-l border-[hsl(var(--border))] pl-4 sm:pl-5" : "pr-4 sm:pr-0"} ${i > 1 ? "md:border-l md:pl-5" : ""}`}>
                  <div className="font-serif text-2xl md:text-3xl gold-text leading-none" data-testid={`hero-stat-${i}`}>{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2 whitespace-pre-line leading-tight">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT — PORTRAIT */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1], delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[3/4] max-w-md mx-auto">
              {/* Decorative gold panel behind */}
              <div className="absolute -top-5 -right-5 w-3/4 h-3/4 rounded-[2rem] bg-gradient-to-br from-[hsl(var(--gold))]/20 to-[hsl(var(--gold-deep))]/10 backdrop-blur" />
              <div className="absolute -bottom-5 -left-5 w-2/3 h-2/3 rounded-[2rem] border border-[hsl(var(--gold))]/40" />

              {/* Image frame */}
              <div className="relative h-full rounded-[2rem] overflow-hidden glass-strong p-2">
                <img
                  src={getImageUrl(aboutMe.profileImageUrl)}
                  alt={personalInfo.fullName}
                  className="w-full h-full object-cover rounded-[1.5rem]"
                  loading="eager"
                  data-testid="hero-image"
                />
                {/* Inner corner ornaments */}
                <div className="absolute top-6 left-6 w-10 h-10 border-t-2 border-l-2 border-[hsl(var(--gold))]" />
                <div className="absolute top-6 right-6 w-10 h-10 border-t-2 border-r-2 border-[hsl(var(--gold))]" />
                <div className="absolute bottom-6 left-6 w-10 h-10 border-b-2 border-l-2 border-[hsl(var(--gold))]" />
                <div className="absolute bottom-6 right-6 w-10 h-10 border-b-2 border-r-2 border-[hsl(var(--gold))]" />

                {/* Bottom gradient + signature */}
                <div className="absolute inset-x-2 bottom-2 rounded-b-[1.5rem] h-32 bg-gradient-to-t from-[hsl(var(--navy-deep))]/85 via-[hsl(var(--navy-deep))]/40 to-transparent pointer-events-none" />
                <div className="absolute bottom-7 left-7 right-7 text-[hsl(var(--ivory))]">
                  <div className="font-serif italic text-sm opacity-80">— {personalInfo.designation}</div>
                  <div className="font-serif text-xl mt-0.5 leading-tight">Unified Voice Communication</div>
                </div>
              </div>

              {/* Floating badge — current role */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
                className="absolute -top-4 left-0 sm:-left-4 md:-left-12 glass-strong rounded-2xl px-4 py-3 flex items-center gap-3"
              >
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-ping" />
                  <span className="relative block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                </div>
                <div className="leading-tight">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground">Available</div>
                  <div className="text-xs font-medium">For consulting</div>
                </div>
              </motion.div>

              {/* Floating badge — accolade */}
              <motion.div
                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.25, duration: 0.7 }}
                className="absolute top-1/3 right-0 sm:-right-6 md:-right-12 glass-strong rounded-2xl px-4 py-3 max-w-[180px]"
              >
                <div className="text-[9px] uppercase tracking-[0.25em] text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]">Centurion Award</div>
                <div className="font-serif text-sm mt-1 leading-tight">147% Target Achieved — ₹73Cr</div>
              </motion.div>

              {/* Bottom hand-written-style note */}
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
                className="absolute -bottom-2 right-6 md:-right-4 rotate-[-4deg] font-serif italic text-xs text-muted-foreground"
              >
                <span className="block text-xl font-normal not-italic gold-text">SR.</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground"
      >
        <span>Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[hsl(var(--gold))] to-transparent" />
      </motion.div>
    </section>
  );
};
